/**
 * Permissions utility — handles Android runtime permissions
 * Only active on Android, returns true on iOS
 */
import { Platform, PermissionsAndroid, Alert } from 'react-native';
import { Logger } from './logger';

export type PermissionType = 'camera' | 'storage' | 'internet';

interface PermissionStatus {
  camera: boolean;
  storage: boolean;
  internet: boolean;
}

export const Permissions = {
  /**
   * Request camera permission (for photo capture)
   */
  async requestCamera(): Promise<boolean> {
    if (Platform.OS !== 'android') return true;

    try {
      Logger.debug('Permissions', 'Requesting CAMERA permission');
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
        {
          title: 'Camera Permission',
          message: 'DigitalCampus needs access to your camera for photo capture',
          buttonNeutral: 'Ask Me Later',
          buttonNegative: 'Cancel',
          buttonPositive: 'OK',
        }
      );
      const success = granted === PermissionsAndroid.RESULTS.GRANTED;
      Logger.debug('Permissions', `Camera permission: ${success ? 'GRANTED' : 'DENIED'}`);
      return success;
    } catch (err) {
      Logger.error('Permissions', 'Failed to request CAMERA permission', err);
      return false;
    }
  },

  /**
   * Request file storage permissions (READ/WRITE_EXTERNAL_STORAGE)
   */
  async requestStorage(): Promise<boolean> {
    if (Platform.OS !== 'android') return true;

    try {
      Logger.debug('Permissions', 'Requesting STORAGE permissions');
      const permissions = [
        PermissionsAndroid.PERMISSIONS.READ_EXTERNAL_STORAGE,
        PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
      ];

      const results = await PermissionsAndroid.requestMultiple(permissions);
      const allGranted = Object.values(results).every(
        r => r === PermissionsAndroid.RESULTS.GRANTED
      );

      Logger.debug('Permissions', `Storage permissions: ${allGranted ? 'GRANTED' : 'SOME DENIED'}`, results);
      return allGranted;
    } catch (err) {
      Logger.error('Permissions', 'Failed to request STORAGE permissions', err);
      return false;
    }
  },

  /**
   * Check all critical permissions
   */
  async checkAllPermissions(): Promise<PermissionStatus> {
    Logger.debug('Permissions', 'Checking all permissions');

    const camera = await this.requestCamera().catch(() => false);
    const storage = await this.requestStorage().catch(() => false);

    const status: PermissionStatus = {
      camera,
      storage,
      internet: true, // Internet is declared in AndroidManifest, no runtime request needed
    };

    Logger.info('Permissions', 'Permission check complete', status);
    return status;
  },

  /**
   * Show a user-friendly alert for permission denials
   */
  showPermissionAlert(permission: PermissionType, onRetry?: () => void) {
    const messages: Record<PermissionType, { title: string; message: string }> = {
      camera: {
        title: 'Camera Access Denied',
        message: 'Please enable camera permissions in Settings to use photo features.',
      },
      storage: {
        title: 'Storage Access Denied',
        message: 'Please enable storage permissions in Settings to download files.',
      },
      internet: {
        title: 'Network Error',
        message: 'Please check your internet connection.',
      },
    };

    const msg = messages[permission];
    Alert.alert(msg.title, msg.message, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Retry', onPress: onRetry },
    ]);
  },
};

export type { PermissionStatus };
